/**
 * ReceivingBankAccountFormModal — cria/edita uma conta de recebimento.
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import {
    createReceivingBankAccount, updateReceivingBankAccount,
} from '../services/receivingBankAccountApi';

interface Props {
    account: any | null;   // null = criar; objeto = editar
    onClose: () => void;
    onSuccess: () => void;
}

const ReceivingBankAccountFormModal: React.FC<Props> = ({ account, onClose, onSuccess }) => {
    const editing = !!account;
    const [form, setForm] = useState({
        exibition_name: account?.exibitionName || '',
        alias_name: account?.aliasName || '',
        bank_name: account?.bankName || '',
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!form.exibition_name.trim() || !form.alias_name.trim()) {
            toast.warn('Nome de exibição e apelido são obrigatórios');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                exibition_name: form.exibition_name.trim(),
                alias_name: form.alias_name.trim(),
                bank_name: form.bank_name.trim() || undefined,
            };
            if (editing) {
                await updateReceivingBankAccount(account.receivingBankAccountId ?? account.id, payload);
                toast.success('Conta atualizada');
            } else {
                await createReceivingBankAccount(payload);
                toast.success('Conta criada');
            }
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e.message || 'Erro ao salvar');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {editing ? 'Editar conta de recebimento' : 'Nova conta de recebimento'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome de exibição</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.exibition_name} onChange={e => setForm({ ...form, exibition_name: e.target.value })}
                            placeholder="Ex.: Itaú SCFI" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apelido (único)</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.alias_name} onChange={e => setForm({ ...form, alias_name: e.target.value })}
                            placeholder="Ex.: ITAU_SCFI" />
                        <p className="text-xs text-gray-400 mt-1">Chave estável usada internamente; evite alterar após uso.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Banco / instituição</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })}
                            placeholder="Ex.: Itaú" />
                    </div>
                </div>

                <div className="mt-5 flex gap-2 justify-end">
                    <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                        {submitting ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReceivingBankAccountFormModal;
